import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import {
  ImportElementType,
  ImportQuestion,
  ImportReport,
  ImportTreeNode,
  REPLACE,
  SKIP,
} from '../../../models/classic-import.model';
import { ICONS } from '../classic-import-tree/classic-import-tree.component';

/** From this many existing elements on, the list starts collapsed. */
const COLLAPSE_FROM = 10;

interface ExistingRow {
  question: ImportQuestion;
  type: ImportElementType | null;
  name: string;
}

/**
 * Confirm step of the Classic import: questions on plaintext passwords and existing elements, below them the counts
 * of the report. A card shows only the text of a question; the BE never sends values, and none is rendered.
 */
@Component({
  selector: 'app-classic-import-confirm',
  imports: [TranslatePipe, MatIcon, MatRadioModule],
  templateUrl: './classic-import-confirm.component.html',
  styleUrl: './classic-import-confirm.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassicImportConfirmComponent {
  readonly report = input.required<ImportReport>();
  readonly tree = input<ImportTreeNode[]>([]);
  readonly answers = input<Record<string, string>>({});
  readonly busy = input(false);
  readonly answersChange = output<Record<string, string>>();

  readonly replace = REPLACE;
  readonly skip = SKIP;
  readonly icons = ICONS;

  readonly encryptQuestions = computed(() => this.report().questions.filter((q) => q.type === 'ENCRYPT_PLAINTEXT'));

  readonly existingRows = computed<ExistingRow[]>(() => {
    const nodes = new Map(this.tree().map((n) => [n.sourceKey, n]));
    return this.report()
      .questions.filter((q) => q.type === 'EXISTING_ELEMENT')
      .map((question) => {
        const node = nodes.get(question.element);
        return { question, type: node?.type ?? null, name: node?.name ?? question.text };
      });
  });

  // Every choice brings a new report; only crossing the limit may reset what the user opened or closed.
  private readonly longList = computed(() => this.existingRows().length >= COLLAPSE_FROM);
  readonly listOpen = linkedSignal(() => !this.longList());

  readonly summary = computed(() => {
    const counts = this.report().counts;
    const created = (type: ImportElementType): number => counts[type]?.created ?? 0;
    // Replaced and skipped cover every type, also test plans, requirements and documents.
    const sum = (field: 'replaced' | 'skipped'): number =>
      Object.values(counts).reduce((total, count) => total + (count?.[field] ?? 0), 0);
    return {
      testCases: created('TEST_CASE'),
      modules: created('TEST_MODULE'),
      dataSheets: created('DATA_SHEET'),
      libraries: created('OBJECT_LIBRARY'),
      replaced: sum('replaced'),
      skipped: sum('skipped'),
    };
  });

  choose(questionId: string, answer: string): void {
    this.answersChange.emit({ ...this.answers(), [questionId]: answer });
  }

  chooseAllExisting(answer: string): void {
    const next = { ...this.answers() };
    for (const row of this.existingRows()) next[row.question.id] = answer;
    this.answersChange.emit(next);
  }
}
